import axios from "axios";
import moment from 'moment';
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router';
import useOffCanvasReducer from 'hooks/useOffCanvasReducer';
import useCalendar from "hooks/useCalendar";
import useEditAppointment from "hooks/useEditAppointment";
import useUrlAction from "hooks/useUrlAction";
import { getUrlParam } from "utils/helpers";
import { selectOrganization } from "store/actions/adminActions";
import { useDispatch } from "react-redux";

export default function useAdminCalendar() {

    const [resources, setResources] = useState();
    const [selectedAppointmentId, dispatchViewAppointment] = useOffCanvasReducer();
    const [showSwitchPhotographers, dispatchShowSwitchPhotographers] = useOffCanvasReducer();
    const [showDisableSlots, dispatchShowDisableSlots] = useOffCanvasReducer();
    const [showAddOrgAppointment, dispatchShowAddOrgAppointment] = useOffCanvasReducer();
    const [showNewBuilding, dispatchShowNewBuilding] = useOffCanvasReducer();
    const [showOffCanvasSettings, dispatchShowOffCanvasSettings] = useOffCanvasReducer();
    const [showAddManualAppointment, dispatchShowAddManualAppointment] = useOffCanvasReducer();
    const [showResourceSelected, dispatchShowResourceSelected] = useOffCanvasReducer();
    const [showAddServiceAppointment, dispatchShowAddServiceAppointment] = useOffCanvasReducer();
    const [resourceInfoSelected, setResourceInfoSelected] = useState();
    const [organizationSelected, setOrganizationSelected] = useState();
    const [organizationAgents, setOrganizationAgents] = useState();
    const [isLoadingOrganizationAgents, setIsLoadingOrganizationAgents] = useState(false);
    const [organizationProducts, setOrganizationProducts] = useState();
    const [isLoadingOrganizationProducts, setIsLoadingOrganizationProducts] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useUrlAction({
        show: [dispatchViewAppointment, [getUrlParam('id', true)]]
    });

    const calendarLogic = useCalendar({
        loadPromise: (start_date, end_date) => axios.get('appointments', {
            params: {
                start: start_date.format('YYYY-MM-DD'),
                end: end_date.format('YYYY-MM-DD'),
                events: 1,
                admin: 1
            }
        }),
        resources: resources,
        isResources: true
    });
    const { 
        checkUpdateAppointment
    } = useEditAppointment();

    const { loadFirst } = calendarLogic;

    useEffect(() => {
        axios.get('users', {
            params: {
                role: 'photographer'
            }
        })
        .then(res => setResources(
            res.data.map(r => ({ 
                id: r.photographer_id,
                uid: r.uid, 
                title: r.display_name, 
                show_priority: r.show_priority 
            }))
        ));
    }, []);

    useEffect(() => {
        if (!resources || !loadFirst) return;
        loadFirst();
    }, [resources, loadFirst]);

    const handleEventClick = (info) => {
        dispatchViewAppointment(info.event.id);
    }

    const handleEventUpdate = info => {
        const { event } = info;
        info.event.setProp('classNames', ['placeholder']);
        const [resource] = event.getResources();
        checkUpdateAppointment({
            id: event.id,
            photographer_id: +resource.id,
            estimated_start_at: moment(event.start).format('YYYY-MM-DD HH:mm'),
            estimated_end_at: moment(event.end).format('YYYY-MM-DD HH:mm')
        })
    }

    const syncSelectedOrganization = (organization = null) => {
        setOrganizationSelected(organization);
        dispatch(selectOrganization(organization));
        if (!organization) {
            setOrganizationAgents();
            setIsLoadingOrganizationAgents(false);
            setOrganizationProducts();
            setIsLoadingOrganizationProducts(false);
        }
    }

    const handleSelectOrganization = organization => {
        syncSelectedOrganization(organization);
    }

    const handleSelectServiceOrganization = (organization, building = undefined) => {
        const orgCopy = { ...(organization || {}) };
        // If we are in the "Aggiungi appuntamento immobile" flow, selecting a building
        // should navigate to the reservation page for that building instead of
        // opening the service offcanvas.
        if (showAddOrgAppointment === 'BUILDING' && building !== undefined && building !== null) {
            // close the add-org offcanvas and navigate to reservation for the building
            dispatchShowAddOrgAppointment('CLOSE');
            return navigate(`/reservation/new/${building.id}`);
        }
        if (building === null) {
            // explicitly clear any selected building
            delete orgCopy.building;
        } else if (building !== undefined) {
            // set provided building
            orgCopy.building = building;
        }
        syncSelectedOrganization(orgCopy);
        // Open service flow and load organization agents/products
        dispatchShowAddOrgAppointment('CLOSE');
        dispatchShowAddServiceAppointment('OPEN');
        setIsLoadingOrganizationAgents(true);
        setIsLoadingOrganizationProducts(true);
        axios.get(`organizations/${organization.id}/agents`)
        .then(res => setOrganizationAgents(res.data?.agents || []))
        .catch(() => setOrganizationAgents([]))
        .finally(() => setIsLoadingOrganizationAgents(false));
        axios.get('products', {
            params: {
                organization_id: organization.id
            }
        })
        .then(res => {
            // const filterParams = building ['PRIMARY', 'EXTRA'];
            const filterParams = building ? ['PRIMARY', 'EXTRA'] : ['PRIMARY', 'EXTRA', 'GENERIC'];
            setOrganizationProducts((res.data || []).filter(product => filterParams.includes(product.type)))
        }
        )
        .catch(() => setOrganizationProducts([]))
        .finally(() => setIsLoadingOrganizationProducts(false));
    }

    const handleSelectResourceInfo = (info) => {
        setResourceInfoSelected(info);
    }

    const handleCloseAddServiceAppointment = () => {
        syncSelectedOrganization();
        dispatchShowAddServiceAppointment('CLOSE');
    }

    const handleBackAddServiceAppointment = () => {
        // go back to building selection for the same organization (do not clear it)
        dispatchShowAddServiceAppointment('CLOSE');
        dispatchShowAddOrgAppointment('SERVICE');
    }

    useEffect(() => {
        if (showNewBuilding) {
            return dispatchShowAddOrgAppointment('CLOSE');
        }
        handleSelectOrganization();
    }, [showNewBuilding]);

    useEffect(() => {
        if (showAddManualAppointment) return dispatchShowResourceSelected('CLOSE');
    }, [showAddManualAppointment]);

    useEffect(() => {
        if (showAddOrgAppointment) return dispatchShowResourceSelected('CLOSE');
    }, [showAddOrgAppointment]);

    useEffect(() => {
        if (showDisableSlots) return dispatchShowResourceSelected('CLOSE');
    }, [showDisableSlots]);

    return {
        handleEventClick,
        resources,
        selectedAppointmentId, dispatchViewAppointment,
        showSwitchPhotographers, dispatchShowSwitchPhotographers,
        showDisableSlots, dispatchShowDisableSlots,
        showAddOrgAppointment, dispatchShowAddOrgAppointment,
        showAddManualAppointment, dispatchShowAddManualAppointment,
        showNewBuilding, dispatchShowNewBuilding,
        showOffCanvasSettings, dispatchShowOffCanvasSettings,
        showResourceSelected, dispatchShowResourceSelected,
        showAddServiceAppointment,
        organizationAgents,
        isLoadingOrganizationAgents,
        organizationProducts,
        isLoadingOrganizationProducts,
        resourceInfoSelected, handleSelectResourceInfo,
        handleSelectOrganization,
        handleSelectServiceOrganization,
        handleCloseAddServiceAppointment,
        handleBackAddServiceAppointment,
        organizationSelected,
        handleEventUpdate,
        //Calendar
        calendarLogic,
        
    }
}
